import {
  Box,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { RoleSelect } from '@/components/RoleSelect';
import {
  SearchableOrganizationSelect,
  SearchableOrganizationSelectValue,
} from '@/components/SearchableOrganizationSelect';
import { EUserRole } from '@/graphql-types/globalTypes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { Card } from '@/imports/ui/chakra/components/Card';
import { CompactCheckbox } from '@/imports/ui/chakra/components/CompactCheckbox';
import { DeprecatedSubtleSwitch } from '@/imports/ui/chakra/components/SubtleSwitch';
import { REG_EXP } from '@/utilities';

export type AccountFormValues = {
  accountEmail?: string;
  shouldSendInvitationEmail?: boolean;
  role: EUserRole;
  organization?: SearchableOrganizationSelectValue;
  isRecordOnly?: boolean;
  oldview?: boolean;
};

export const AccountForm: React.FC<{ isLoading: boolean; oldview?: boolean }> = ({
  isLoading,
  oldview = true,
}) => {
  const memberManagement = useMemberManagement();
  const {
    register,
    watch,
    formState: { errors },
    control,
    getValues,
  } = useFormContext<AccountFormValues>();

  const watchShouldSendInvitationEmail = watch('shouldSendInvitationEmail');
  const watchRole = watch('role');
  const watchIsRecordOnly = watch('isRecordOnly');

  const isRoleOrganizationAdmin =
    watchRole === EUserRole.ORGANIZATION_ACCOUNT_OWNER ||
    watchRole === EUserRole.ORGANIZATION_PAGE_ADMIN;

  return (
    <Card
      p={oldview ? 0 : 6}
      boxShadow={oldview ? 'none' : 'base'}
      borderWidth={oldview ? 0 : '1px'}
    >
      <Stack spacing="0">
        <Stack spacing="6" p={oldview ? 0 : 6}>
          {!oldview && <Text textStyle="xlBodyStrong">Account</Text>}

          <FormControl
            isInvalid={!!errors.accountEmail}
            isDisabled={watchIsRecordOnly || isLoading}
            isRequired={!watchIsRecordOnly}
          >
            <FormLabel>Account Email</FormLabel>
            <Input
              placeholder="name@gmail.com"
              {...register('accountEmail', {
                validate: {
                  email: (value) =>
                    getValues('isRecordOnly') ||
                    (!!value && REG_EXP.EmailWithTLD.test(value)) ||
                    'You must enter a valid email address',
                },
              })}
            />
            <FormHelperText>
              The email this person will use to log in to Tradewing
            </FormHelperText>
            <FormErrorMessage>{errors.accountEmail?.message}</FormErrorMessage>
          </FormControl>

          <FormControl
            isDisabled={watchIsRecordOnly || isLoading}
            isRequired={!watchIsRecordOnly}
          >
            <FormLabel>Role</FormLabel>
            <Controller
              control={control}
              name="role"
              render={({ field: { value, onChange, ref } }) => (
                <RoleSelect
                  maxWidth="3xs"
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
              rules={{
                deps: ['organization'],
              }}
            />
          </FormControl>

          {isRoleOrganizationAdmin && (
            <FormControl
              isInvalid={!!errors.organization}
              isDisabled={watchIsRecordOnly || isLoading}
              isRequired={isRoleOrganizationAdmin && !watchIsRecordOnly}
            >
              <FormLabel>Organization Name</FormLabel>
              <Controller
                control={control}
                name="organization"
                render={({ field: { value, onChange, ref } }) => (
                  <SearchableOrganizationSelect
                    value={value}
                    onChange={onChange}
                    ref={ref}
                    allowNewOrganizationCreation
                  />
                )}
                rules={{
                  validate: {
                    required: (value) =>
                      (getValues('role') !==
                        EUserRole.ORGANIZATION_ACCOUNT_OWNER &&
                        getValues('role') !==
                          EUserRole.ORGANIZATION_PAGE_ADMIN) ||
                      !!value ||
                      'This is a required field',
                  },
                }}
              />
              <FormErrorMessage>{errors.organization?.message}</FormErrorMessage>
            </FormControl>
          )}

          <FormControl
            isDisabled={watchIsRecordOnly || isLoading}
            marginBottom={oldview ? 0 : 6}
          >
            <FormLabel>Send Welcome Email</FormLabel>
            <Controller
              control={control}
              name="shouldSendInvitationEmail"
              render={({ field: { value, onChange, ref } }) => (
                <DeprecatedSubtleSwitch
                  isChecked={value}
                  onChange={() => onChange(!value)}
                  ref={ref}
                >
                  {watchShouldSendInvitationEmail ? 'Yes' : 'No'}
                </DeprecatedSubtleSwitch>
              )}
            />
          </FormControl>
        </Stack>

        {memberManagement.isActive && (
          <Box paddingTop={6} pl={oldview ? 0 : 6}>
            <Controller
              name="isRecordOnly"
              rules={{
                deps: [
                  'accountEmail',
                  'shouldSendInvitationEmail',
                  'role',
                  'organization',
                ],
              }}
              render={({ field: { value, onChange, ref, onBlur } }) => (
                <CompactCheckbox
                  isChecked={value}
                  onChange={(event) => onChange(event.target.checked)}
                  ref={ref}
                  onBlur={onBlur}
                  isDisabled={isLoading}
                >
                  {`Don't create an account for this person`}
                </CompactCheckbox>
              )}
            />
          </Box>
        )}

        <Divider paddingTop={6} />
      </Stack>
    </Card>
  );
};
